import { getUserTimestamp } from './userstamp';
import { browserLogger } from '@widget/utils/datadog';
import { isObject } from '@widget/toolbox/utils';
import { GA_TRACKING_PARAMS_NAME } from '@widget/consts';

import type { ApiResponse, AdditionalQueryParams, WidgetMetaData, LeadDataResponse } from './types';
import type { Flags } from '@widget/core';

type LeadResponseSuccess = {
  data: LeadDataResponse;
  error: false;
};

/**
 * Makes a POST request to the widget-lite-service (https://git.mcmakler.com/nodejs/widget-lite-service).
 * In its payload, the incoming data from the widget steps is sent, alongside meta data of
 * the widget such as name, id, tracking parameters, etc.
 */
export async function sendLeadData(
  widgetFormData: Record<string, unknown>,
  additionalParams?: AdditionalQueryParams,
): Promise<LeadResponseSuccess | void> {
  const widgetMetaData = createWidgetMetaData(additionalParams);
  const { booking, ...restWidgetFormData } = widgetFormData;
  const body = JSON.stringify({
    type: 'short-valuation',
    payload: { ...widgetMetaData, ...restWidgetFormData },
    booking,
  });

  return fetch('/api-widget/lead', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
  })
    .then(handleErrors)
    .then((res) => res.json());
}

export async function updateLeadData(
  leadId: string,
  widgetFormData: Record<string, unknown>,
): Promise<ApiResponse<LeadDataResponse, Record<string, any>>> {
  try {
    const body = JSON.stringify(widgetFormData);
    const response = await fetch(`/api-widget/lead/${leadId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });
    return await response.json();
  } catch (error) {
    browserLogger.error(error, 'Lead update fail');
    return { error: true, errorData: { message: 'unknown error' } };
  }
}

function getTrackingParamsFromGA() {
  try {
    if (!(GA_TRACKING_PARAMS_NAME in window)) {
      throw new Error(`window.${GA_TRACKING_PARAMS_NAME} is not defined`);
    }

    if (!isObject(window[GA_TRACKING_PARAMS_NAME])) {
      throw new Error(`window.${GA_TRACKING_PARAMS_NAME} is not an object`);
    }

    return window[GA_TRACKING_PARAMS_NAME];
  } catch (error) {
    browserLogger.error(error, 'widget get tracking params for GA');

    return {};
  }
}

function createWidgetMetaData(additionalParams?: AdditionalQueryParams): WidgetMetaData {
  return {
    ...getTrackingParamsFromGA(),
    userTimestamp: getUserTimestamp(),
    widgetName: additionalParams?.widgetName || 'WidgetX',
    widgetId: additionalParams?.widgetId || 'default',
    agent: additionalParams?.agent || '',
    flags: additionalParams?.flags || ({} as Flags),
    referrer: encodeURIComponent(window.location.href),
  };
}

export function handleErrors(res: Response): Response {
  if (!res.ok) {
    throw new Error(res.statusText);
  }
  return res;
}
